import React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import {
  ADDRESS_CITY_MAX_LENGTH,
  ADDRESS_POST_CODE_MAX_LENGTH,
  ADDRESS_STREET_MAX_LENGTH,
  ADDRESS_STREET_NUMBER_MAX_LENGTH,
  IDENTIFICATION_NUMBER_MAX_LENGTH,
  TRANSIT_PARTY_NAME_MAX_LENGTH,
} from 'shared/ncts/common/constants'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import TypeOfPerson from 'features/declaration/form/components/TypeOfPerson'
import FormField from '../../../../components/p5/FormField'
import FormSelect from '../../../../components/p5/FormSelect'
import { getSelectCountryFilter } from '../../../../components/react-select/CountryFunctions'
import Input from '../../../../components/p5/Input'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'

const scope = 'seller'

function Seller() {
  const { t } = useTranslation()
  const [, countriesOptions] = useCodelist('COUNTRIES')
  const { control } = useFormContext<HouseConsignmentFormValues>()

  const isDifferentSeller = useWatch({
    control,
    name: 'differentSeller',
  })

  const fieldRequirements = useDeclarationFieldRequirements<HouseConsignmentFormValues>('houseConsignment', [
    'differentSeller',
    scope,
    'seller.identificationNumber',
    'seller.name',
    'seller.typeOfPerson',
    'seller.address.country',
    'seller.address.street',
    'seller.address.streetNumber',
    'seller.address.city',
    'seller.address.postcode',
    'seller.contactPerson.email',
    'seller.contactPerson.telephone',
  ])

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-seller"
        headingLabel={t('declaration.p5.seller')}
        fieldRule={fieldRequirements[scope]}
      >
        <div className="row mb-2">
          <div className="d-flex mb-2 col">
            <Input
              field="differentSeller"
              type="checkbox"
              labelKey="Seller is different from cosnignor"
              fieldRule={fieldRequirements.differentSeller}
            />
          </div>
        </div>
        {isDifferentSeller && (
          <>
            <FormField
              labelKey="declaration.p5.eori"
              fieldRule={fieldRequirements[`${scope}.identificationNumber`]}
            >
              <Input
                field={`${scope}.identificationNumber`}
                autoComplete={`${scope}.identificationNumber`}
                labelKey="declaration.p5.identificationNumber"
                type="text"
                maxLength={IDENTIFICATION_NUMBER_MAX_LENGTH}
                placeholder={t('declaration.p5.eori')}
                fieldRule={fieldRequirements[`${scope}.identificationNumber`]}
              />
            </FormField>
            <FormField
              labelKey="declaration.p5.name"
              fieldRule={fieldRequirements[`${scope}.name`]}
            >
              <Input
                field={`${scope}.name`}
                autoComplete={`${scope}.name`}
                labelKey="declaration.p5.name"
                type="text"
                maxLength={TRANSIT_PARTY_NAME_MAX_LENGTH}
                placeholder={t('declaration.p5.name')}
                fieldRule={fieldRequirements[`${scope}.name`]}
              />
            </FormField>
            <TypeOfPerson
              scope={scope}
              fieldRule={fieldRequirements[`${scope}.typeOfPerson`]}
            />
            <FormField
              labelKey="declaration.p5.country"
              fieldRule={fieldRequirements[`${scope}.address.country`]}
            >
              <FormSelect
                field={`${scope}.address.country`}
                labelKey="declaration.p5.country"
                options={countriesOptions}
                customFilterOption={getSelectCountryFilter}
                type="sync"
                fieldRule={fieldRequirements[`${scope}.address.country`]}
              />
            </FormField>
            <div className="row party-address-row">
              <FormField
                className="col-6"
                labelKey="declaration.p5.street"
                fieldRule={fieldRequirements[`${scope}.address.street`]}
              >
                <Input
                  <HouseConsignmentFormValues>
                  field={`${scope}.address.street`}
                  autoComplete={`${scope}.address.street`}
                  labelKey="declaration.p5.street"
                  type="text"
                  maxLength={ADDRESS_STREET_MAX_LENGTH}
                  placeholder={t('declaration.p5.street')}
                  fieldRule={fieldRequirements[`${scope}.address.street`]}
                />
              </FormField>
              <FormField
                className="col-6"
                labelKey="declaration.p5.streetNumber"
                fieldRule={fieldRequirements[`${scope}.address.streetNumber`]}
              >
                <Input
                  field={`${scope}.address.streetNumber`}
                  autoComplete={`${scope}.address.streetNumber`}
                  labelKey="declaration.p5.streetNumber"
                  type="text"
                  maxLength={ADDRESS_STREET_NUMBER_MAX_LENGTH}
                  placeholder={t('declaration.p5.streetNumber')}
                  fieldRule={fieldRequirements[`${scope}.address.streetNumber`]}
                />
              </FormField>
            </div>
            <div className="row party-address-row">
              <FormField
                className="col-8"
                labelKey="declaration.p5.city"
                fieldRule={fieldRequirements[`${scope}.address.city`]}
              >
                <Input
                  field={`${scope}.address.city`}
                  autoComplete={`${scope}.address.city`}
                  labelKey="declaration.p5.city"
                  maxLength={ADDRESS_CITY_MAX_LENGTH}
                  type="text"
                  placeholder={t('declaration.p5.city')}
                  fieldRule={fieldRequirements[`${scope}.address.city`]}
                />
              </FormField>
              <FormField
                className="col-4"
                labelKey="declaration.p5.postcode"
                fieldRule={fieldRequirements[`${scope}.address.postcode`]}
              >
                <Input
                  field={`${scope}.address.postcode`}
                  autoComplete={`${scope}.address.postcode`}
                  labelKey="declaration.p5.postcode"
                  maxLength={ADDRESS_POST_CODE_MAX_LENGTH}
                  type="text"
                  placeholder={t('declaration.p5.postcode')}
                  fieldRule={fieldRequirements[`${scope}.address.postcode`]}
                />
              </FormField>
            </div>
            <FormField
              labelKey="declaration.p5.contactPersonPhone"
              fieldRule={fieldRequirements[`${scope}.contactPerson.telephone`]}
            >
              <Input
                field={`${scope}.contactPerson.telephone`}
                autoComplete={`${scope}.contactPerson.telephone`}
                labelKey="declaration.p5.contactPersonPhone"
                maxLength={35}
                type="text"
                placeholder={t('declaration.p5.contactPersonPhone')}
                fieldRule={fieldRequirements[`${scope}.contactPerson.telephone`]}
              />
            </FormField>
            <FormField
              labelKey="declaration.p5.contactPersonEmail"
              fieldRule={fieldRequirements[`${scope}.contactPerson.email`]}
            >
              <Input
                field={`${scope}.contactPerson.email`}
                autoComplete={`${scope}.contactPerson.email`}
                labelKey="declaration.p5.contactPersonEmail"
                maxLength={256}
                type="text"
                placeholder={t('declaration.p5.contactPersonEmail')}
                fieldRule={fieldRequirements[`${scope}.contactPerson.email`]}
              />
            </FormField>
          </>
        )}
      </FormCollapsibleColumn>
    </section>
  )
}

export default Seller
